import React from 'react';

import { useUserStatusContext } from '../../context/UserStatus';
import useGetUsersList from '../UsersManagement/api/useGetUsersList';

export type ActorName = (actor: string | number) => string;

const actorId = (actor: string | number): number | undefined => {
    if (typeof actor === 'number')
        return Number.isSafeInteger(actor) && actor > 0 ? actor : undefined;
    const match = /^hero_user:(\d+)$/.exec(actor);
    const id = match ? Number(match[1]) : undefined;
    return id !== undefined && Number.isSafeInteger(id) && id > 0 ? id : undefined;
};

const useCareActorName = (actors: (string | number)[]) => {
    const { userStatus, permissions } = useUserStatusContext();
    const hasTeamActors = actors.some(
        (actor) => actorId(actor) !== undefined && actorId(actor) !== userStatus?.hero_user_id
    );
    const directoryQuery = React.useMemo(() => ({ limit: 100, offset: 0 }), []);
    const directory = useGetUsersList(directoryQuery, {
        enabled: permissions.iam.listInvites && hasTeamActors,
        allPages: true,
        retry: false
    });
    const names = new Map<number, string>();
    directory.data?.items.forEach((user) => {
        const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.email;
        if (user.hero_user_id !== null && name) names.set(user.hero_user_id, name);
    });
    if (userStatus) {
        const name = [userStatus.first_name, userStatus.last_name].filter(Boolean).join(' ').trim() || userStatus.email;
        if (name) names.set(userStatus.hero_user_id, name);
    }
    const actorName: ActorName = (actor) => {
        const id = actorId(actor);
        if (id !== undefined)
            return names.get(id) ?? (directory.isFetching ? 'Loading team member name…' : 'Team member — name unavailable');
        if (typeof actor === 'number' || !actor || actor === 'unknown') return 'Name not recorded';
        if (/^(system|cron|scheduled|care_population|care_qualification|care_readiness|hwm23270-synthetic|cli:)/.test(actor))
            return 'System';
        return 'Name not available';
    };
    return { actorName, directoryError: directory.error, retryNames: directory.refetch };
};

export default useCareActorName;
